/**
 * @file notificationUtils.js
 * @description PWA notification utilities for managing local storage settings and scheduling
 */

/**
 * Default notification settings
 */
const DEFAULT_NOTIFICATION_SETTINGS = {
  enabled: false,
  time: 7, // 7 AM in 24-hour format
  permission: 'default'
};

/**
 * Get notification settings from localStorage
 * @returns {Object} Notification settings object
 */
export const getNotificationSettings = () => {
  try {
    const settings = localStorage.getItem('notificationSettings');
    return settings ? JSON.parse(settings) : DEFAULT_NOTIFICATION_SETTINGS;
  } catch (error) {
    console.error('Error reading notification settings:', error);
    return DEFAULT_NOTIFICATION_SETTINGS;
  }
};

/**
 * Update notification settings in localStorage
 * @param {Object} updates - Settings to update
 * @returns {Object} Updated settings object
 */
export const updateNotificationSettings = (updates) => {
  try {
    const current = getNotificationSettings();
    const updated = { ...current, ...updates };
    localStorage.setItem('notificationSettings', JSON.stringify(updated));
    return updated;
  } catch (error) {
    console.error('Error updating notification settings:', error);
    return getNotificationSettings();
  }
};

/**
 * Request notification permission from the browser
 * @returns {Promise<string>} Permission status ('granted', 'denied', or 'default')
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    updateNotificationSettings({ permission });
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
};

/**
 * Check if notifications are supported and enabled
 * @returns {boolean} True if notifications can be sent
 */
export const canSendNotifications = () => {
  const settings = getNotificationSettings();
  return (
    'Notification' in window &&
    'serviceWorker' in navigator &&
    settings.enabled &&
    Notification.permission === 'granted'
  );
};

/**
 * Calculate the next notification time based on user preference
 * @param {number} hour - Hour in 24-hour format (0-23)
 * @returns {Date} Next notification time
 */
export const getNextNotificationTime = (hour) => {
  const now = new Date();
  const notificationTime = new Date();
  notificationTime.setHours(hour, 0, 0, 0);
  
  // If the time has already passed today, schedule for tomorrow
  if (notificationTime <= now) {
    notificationTime.setDate(notificationTime.getDate() + 1);
  }
  
  return notificationTime;
};

/**
 * Schedule a daily notification
 * @param {number} hour - Hour to send notification (0-23)
 * @param {Array} userChallenges - User's active challenges
 */
export const scheduleDailyNotification = async (hour, userChallenges) => {
  if (!canSendNotifications()) {
    console.warn('Cannot send notifications - not supported or not enabled');
    return;
  }

  try {
    // Cancel any existing notifications
    await cancelScheduledNotifications();

    const nextTime = getNextNotificationTime(hour);
    const delay = nextTime.getTime() - Date.now();

    // Schedule the notification
    setTimeout(async () => {
      // Import hasAnyIncompleteReadingsToday to check if notification should be sent
      const { hasAnyIncompleteReadingsToday } = await import('./visual_utils.js');
      
      if (hasAnyIncompleteReadingsToday(userChallenges)) {
        await showNotification(
          'Reading Reminder',
          'You have readings due for today!',
          {
            icon: '/logo192.png',
            badge: '/logo192.png',
            tag: 'daily-reading',
            renotify: true,
            actions: [
              {
                action: 'view',
                title: 'View Readings'
              }
            ]
          }
        );
      }

      // Schedule next day's notification
      scheduleDailyNotification(hour, userChallenges);
    }, delay);

    console.log(`Notification scheduled for ${nextTime.toLocaleString()}`);
  } catch (error) {
    console.error('Error scheduling notification:', error);
  }
};

/**
 * Show a notification using service worker if available, fallback to browser API
 * @param {string} title - Notification title
 * @param {string} body - Notification body
 * @param {Object} options - Notification options
 */
export const showNotification = async (title, body, options = {}) => {
  if (!canSendNotifications()) {
    return;
  }

  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, {
        body,
        icon: '/logo192.png',
        badge: '/logo192.png',
        ...options
      });
    } else {
      // Fallback to browser notification API
      new Notification(title, {
        body,
        icon: '/logo192.png',
        ...options
      });
    }
  } catch (error) {
    console.error('Error showing notification:', error);
  }
};

/**
 * Cancel all scheduled notifications
 */
export const cancelScheduledNotifications = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      const notifications = await registration.getNotifications();
      notifications.forEach(notification => notification.close());
    }
  } catch (error) {
    console.error('Error canceling notifications:', error);
  }
};

/**
 * Initialize notification system
 * @param {Array} userChallenges - User's active challenges
 */
export const initializeNotifications = async (userChallenges = []) => {
  const settings = getNotificationSettings();
  
  if (settings.enabled) {
    const permission = await requestNotificationPermission();
    if (permission === 'granted') {
      await scheduleDailyNotification(settings.time, userChallenges);
      return true;
    } else {
      // Disable notifications if permission was denied
      updateNotificationSettings({ enabled: false });
      return false;
    }
  }
  
  return false;
};
